import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { useLazyLoadQuery } from 'react-relay';
import { STATS_SNAPSHOT_QUERY } from 'graphql/StatsSnapshotQuery';
import { PROFILE_BADGES_QUERY } from 'graphql/ProfileBadgesQuery';
import useIsPersonalView from 'utils/useIsPersonalView';
import ElevatedContainer from 'components/common/section/ElevatedContainer';
import { StatsSnapshotQuery } from 'graphql/__generated__/StatsSnapshotQuery.graphql';
import { ProfileBadgesQuery } from 'graphql/__generated__/ProfileBadgesQuery.graphql';
import Fallback from 'components/common/fallback/Fallback';
import { logBoundaryError } from 'services/logger';
import { ErrorBoundary } from 'react-error-boundary';
import SectionSummaryContainer from 'components/common/section/SectionSummaryContainer';
import { LoadingMainPanelSection } from 'components/common/suspense-fallbacks/LoadingMainPanelSection';
import { Suspense } from 'react';
import useIsLoggedIn from 'utils/useIsLoggedIn';
import { getForumsSignInUrl } from 'utils/url-generator';
import { formatSnapshotValues } from 'utils/formatAvatarRemainderNumber';
import useLoggedInUser from 'utils/useLoggedInUser';
import { trackStatsSnapshotLinkClick } from 'analytics/profileMainPanelAnalytics';

export const StatsSnapshotWrapper = ({
  internalId,
  learnId,
  profileHasConsented,
}: {
  internalId: string;
  learnId?: string;
  profileHasConsented: boolean;
}) => (
  <ErrorBoundary FallbackComponent={Fallback} onError={logBoundaryError}>
    <Suspense fallback={<LoadingMainPanelSection hasHeader={false} />}>
      <StatsSnapshot
        internalId={internalId as string}
        learnId={learnId}
        profileHasConsented={profileHasConsented}
      />
    </Suspense>
  </ErrorBoundary>
);

const StatsSnapshot = ({
  internalId,
  learnId,
  profileHasConsented,
}: {
  internalId: string;
  learnId?: string;
  profileHasConsented: boolean;
}) => {
  const stats = new Map([
    ['answersCreated', { label: 'Answers', value: 0 }],
    ['acceptedAnswersCreated', { label: 'Accepted solutions', value: 0 }],
    ['articlesCreated', { label: 'Articles', value: 0 }],
    ['kudosReceived', { label: 'Likes received', value: 0 }],
    ['coursesCompleted', { label: 'Courses completed', value: 0 }],
    ['totalPosts', { label: 'Total posts', value: 0 }],
  ]);

  const { email } = useLoggedInUser() || {};
  const isPersonalView = useIsPersonalView(internalId);
  const isLoggedIn = useIsLoggedIn();
  const statsSnapshotResponse = useLazyLoadQuery<StatsSnapshotQuery>(
    STATS_SNAPSHOT_QUERY,
    { internalId },
    { fetchPolicy: 'store-or-network' },
  );

  let totalCourses = 0;
  if (learnId) {
    const badgesResponse = useLazyLoadQuery<ProfileBadgesQuery>(
      PROFILE_BADGES_QUERY,
      { internalId },
    ).unified?.unifiedProfile;
    const totalLearningBadges =
      badgesResponse?.learning?.recentCoursesBadges?.totalCount ?? 0;
    totalCourses = totalLearningBadges;
  }

  const forumsData =
    statsSnapshotResponse?.unified?.unifiedProfile?.forums?.snapshot;
  // temp fix for forums user id
  const forumsNodeId =
    statsSnapshotResponse?.unified?.unifiedProfile?.forums?.id;
  const forumsUserIdParts = (forumsNodeId ?? '').split('::forums/');
  const forumsUserId = forumsUserIdParts?.[1] ?? '';

  let forumsPostActivityUrl: string;
  if (forumsData?.id) {
    const postsActivityUrl =
      window.location.origin +
      `/forums/user/viewprofilepage/user-id/${forumsUserId}?show=activity#details`;
    forumsPostActivityUrl = isLoggedIn
      ? getForumsSignInUrl({
          redirectUrl: encodeURIComponent(postsActivityUrl),
          email: email || '',
        })
      : postsActivityUrl;
  }

  let isAllStatsZero = true;
  if (forumsData) {
    Object.entries(forumsData).forEach(([key, value]) => {
      const matchingStat = stats.get(key);
      if (matchingStat && typeof value === 'number') {
        if (value > 0) isAllStatsZero = false;
        matchingStat.value = value;
        stats.set(key, matchingStat);
      }
    });
  }

  const coursesCompletedStat = stats.get('coursesCompleted');
  if (coursesCompletedStat) {
    if (totalCourses > 0) isAllStatsZero = false;
    coursesCompletedStat.value = totalCourses;
    stats.set('coursesCompleted', coursesCompletedStat);
  }

  if (!profileHasConsented) {
    stats.delete('coursesCompleted');
  }

  if (!isPersonalView && isAllStatsZero) {
    return null;
  }

  return (
    <SectionSummaryContainer>
      <ElevatedContainer>
        {/* sorting logic to ensure the correct order of stats is preserved. Zero values moved to the end in the correct order. */}

        <div css={statsContainerStyles} data-testid="snapshot-container">
          {Array.from(stats.entries())
            .sort(([keyA, { value: valueA }], [keyB, { value: valueB }]) => {
              // Move zero values to the end

              if (valueA === 0 && valueB !== 0) return 1;
              if (valueA !== 0 && valueB === 0) return -1;
              // Maintain original order if both are zero or both are non-zero

              return 0;
            })
            .map(([key, { label, value }]) =>
              !isPersonalView && value === 0 ? null : (
                <div css={scoreStyle} key={key}>
                  {key === 'totalPosts' && forumsPostActivityUrl ? (
                    value > 0 ? (
                      <a
                        href={forumsPostActivityUrl}
                        css={plainLinkStyle}
                        onClick={() =>
                          trackStatsSnapshotLinkClick(
                            key,
                            value,
                            isPersonalView,
                          )
                        }
                      >
                        <div
                          data-testid="stat-value-container"
                          css={[
                            numberStyleTotalPosts,
                            isAllStatsZero && numberStyleAllZero,
                            value === 0 && zeroScoreStyle,
                          ]}
                        >
                          {formatSnapshotValues(value)}
                        </div>
                        <div
                          data-testid="stat-label-container"
                          css={labelStyle}
                        >
                          {label}
                        </div>
                      </a>
                    ) : (
                      <div css={scoreStyle}>
                        <div
                          data-testid="stat-value-container"
                          css={[
                            numberStyleTotalPosts,
                            isAllStatsZero && numberStyleAllZero,
                            value === 0 && zeroScoreStyle,
                          ]}
                        >
                          {formatSnapshotValues(value)}
                        </div>
                        <div
                          data-testid="stat-label-container"
                          css={labelStyle}
                        >
                          {label}
                        </div>
                      </div>
                    )
                  ) : (
                    <>
                      <div
                        data-testid="stat-value-container"
                        css={[
                          numberStyle,
                          isAllStatsZero && numberStyleAllZero,
                          value === 0 && zeroScoreStyle,
                        ]}
                      >
                        {formatSnapshotValues(value)}
                      </div>
                      <div data-testid="stat-label-container" css={labelStyle}>
                        {label}
                      </div>
                    </>
                  )}
                </div>
              ),
            )}
        </div>
      </ElevatedContainer>
    </SectionSummaryContainer>
  );
};

export default StatsSnapshot;

const scoreStyle = css({
  textAlign: 'center',
  '@media (max-width: 768px)': {
    marginBottom: '20px',
  },
});

const zeroScoreStyle = css({
  color: token('color.text.subtle'),
});

const numberStyleTotalPosts = css({
  color: token('color.text.brand'),
  lineHeight: 1,
  fontFamily: token('font.family.brand.heading'),
  fontSize: '48px',
  fontStyle: 'normal',
  fontWeight: token('font.weight.semibold'),
  letterSpacing: -0.3,
});

const numberStyle = css({
  color: token('color.text'),
  lineHeight: 1,
  fontFamily: token('font.family.brand.heading'),
  fontSize: '48px',
  fontStyle: 'normal',
  fontWeight: token('font.weight.semibold'),
  letterSpacing: -0.3,
});

const numberStyleAllZero = css({
  color: token('color.text.subtle'),
});

const labelStyle = css({
  color: token('color.text'),
  textAlign: 'center',
  fontFamily: token('font.family.brand.heading'),
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: token('font.weight.semibold'),
  width: '84px',
  margin: '0 auto',
  lineHeight: '20px',
  letterSpacing: 0,
});

const statsContainerStyles = css({
  display: 'grid',
  gridTemplateColumns: 'repeat(6, 1fr)',
  '@media (max-width: 767px)': {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
});

const plainLinkStyle = css({
  color: 'inherit',
  textDecoration: 'none',
  display: 'block', // Ensures the whole block is clickable
  '&:hover': {
    color: 'inherit', // Prevent color change on hover
    textDecoration: 'none',
  },
});
